// The sprint view's numbers (`/sprints`): which sprints the book holds, a sprint's rows, and the
// totals over them.
//
// Every rule is the board's own, read from where the board keeps it, so the sprint view and My
// Accounts can never disagree about one account:
//   - Worked       `touches > 0`, the Activity filter's "Contacted"
//   - Replied      `replyReceivedByAnyone`, the Reply filter's "To anyone" (auto-replies excluded)
//   - Meeting      `meetingBooked`: a meeting counted (the manual meetings of migration 075
//                  included, joined on serve) or one booked ahead
//   - Pipeline     `strategistsOpportunities`: deals created on or after the owner's first sent
//                  touch (the Opportunity filter and Activity's credit), open ones, summing
//                  `qualified_pipeline` (null until stage 2, so a fresh deal adds nothing)
// They are all-time, as on the board: an account that replied before its sprint started counts.
//
// Pure, and imported by `node --test`.

import type { AccountPool, BookAccount, Opportunity } from '../api/types';
import { meetingBooked } from './boardPref.ts';
import { replyReceivedByAnyone, strategistsOpportunities } from './filters.ts';
import { sprintsOf } from './sprints.ts';

export interface SprintOption {
  key: string;
  label: string;
  starts_on: string;
  ends_on: string;
  owner: string;
  vertical: string;
  // Sprint accounts on the book.
  count: number;
}

// Every sprint some row is in, newest start first (the current week on top), each with its row count.
export function sprintOptions(rows: BookAccount[]): SprintOption[] {
  const byKey = new Map<string, SprintOption>();
  for (const row of rows) {
    for (const sprint of sprintsOf(row)) {
      const option = byKey.get(sprint.key);
      if (option) option.count += 1;
      else byKey.set(sprint.key, optionOf(sprint));
    }
  }
  return [...byKey.values()].sort(newestStartFirst);
}

function optionOf(sprint: AccountPool): SprintOption {
  return {
    key: sprint.key, label: sprint.label, starts_on: sprint.starts_on ?? '', ends_on: sprint.ends_on ?? '',
    owner: sprint.owner ?? '', vertical: sprint.vertical ?? '', count: 1,
  };
}

function newestStartFirst(first: SprintOption, second: SprintOption): number {
  return second.starts_on.localeCompare(first.starts_on);
}

// The `?sprint=` key when the book has it, else the newest sprint, else ''.
export function pickedSprintKey(options: SprintOption[], wanted: string | null): string {
  if (wanted && options.some(option => option.key === wanted)) return wanted;
  return options[0]?.key ?? '';
}

export function rowsInSprint<Row extends BookAccount>(rows: Row[], sprintKey: string): Row[] {
  return rows.filter(row => sprintsOf(row).some(sprint => sprint.key === sprintKey));
}

// The owner's credited open deals: the Opportunity filter's "Any open" set.
export function creditedOpenDeals(row: BookAccount): Opportunity[] {
  return strategistsOpportunities(row).filter(deal => !deal.closed);
}

export function creditedPipeline(row: BookAccount): number {
  let total = 0;
  for (const deal of creditedOpenDeals(row)) total += deal.qualified_pipeline ?? 0;
  return total;
}

export function isWorked(row: BookAccount): boolean {
  return (row.touches ?? 0) > 0;
}

export interface SprintTotals {
  // Accounts, each counted once however many touches, replies or deals it has.
  loaded: number;
  worked: number;
  replied: number;
  meetings: number;
  with_opportunity: number;
  // Qualified pipeline in dollars over the credited open deals.
  pipeline: number;
}

export function sprintTotals(rows: BookAccount[]): SprintTotals {
  const totals: SprintTotals = { loaded: 0, worked: 0, replied: 0, meetings: 0, with_opportunity: 0, pipeline: 0 };
  for (const row of rows) {
    totals.loaded += 1;
    if (isWorked(row)) totals.worked += 1;
    if (replyReceivedByAnyone(row)) totals.replied += 1;
    if (meetingBooked(row)) totals.meetings += 1;
    if (creditedOpenDeals(row).length > 0) totals.with_opportunity += 1;
    totals.pipeline += creditedPipeline(row);
  }
  return totals;
}

// "41%": a share of the loaded accounts, '' when there are none.
export function shareOf(count: number, loaded: number): string {
  if (loaded === 0) return '';
  return `${Math.round((count / loaded) * 100)}%`;
}
